
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import Component from "vue-class-component";
import Vue from "vue";
import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
import {
    DocumentAttributesClient,
    ApplicableCertificatesClient,
    CertificateDocumentTypeApiModel,
    StartApplyingForCertificateRequest,
    StartApplyingOrChangeTypeClient,
    ChangeTypeToCertificateRequest,
} from "@/api-client"
import Loader from "@/features/shared/components/Loader";
import { Prop } from "vue-property-decorator";
import userInfo from "@/features/shared/components/UserInfo";

class CertificateType {
    constructor(public id: string, public name: string, public legalName: string, public certificateKind: string) {
    }
}

interface Props {
    seafarerId: string;
    applicationId?: string;
    btnClass: string;
}

@Component({
    components: {
        HeadingWave,
        SectionPageHeading,
        TableEmptySlot
    }
})
export default class ApplicationTypeTable extends Vue implements Props {
    @Prop({ required: true })
    seafarerId!: string;
    @Prop({ required: false })
    applicationId!: string;
    @Prop({ required: false, default: 'btn-wide' })
    btnClass!: string;

    applicationTypes: CertificateType[] = [];
    documentTypes: CertificateDocumentTypeApiModel[] = [{ code: '', name: this.$ls.Global_Filter_Option_AllDocumentTypes } as CertificateDocumentTypeApiModel];
    selectedApplicationType = this.documentTypes[0];
    searchInput = '';

    loader = new Loader();

    created(): void {
        new ApplicableCertificatesClient()
            .get()
            .then(result => this.applicationTypes = result.map(r => new CertificateType(r.templateId, r.name, r.legalName, r.certificateKindId)));

        new DocumentAttributesClient()
            .certificateDocumentTypes()
            .then(result => {
                // Exclude SLB (Soodustusluba/Dispensation) from dropdown - handled separately elsewhere
                const filteredTypes = result.filter(dt => dt.code !== 'SLB');
                this.documentTypes = this.documentTypes.concat(filteredTypes);
                // Set default to "Merelaev" (MLV) if available
                const mlvType = this.documentTypes.find(dt => dt.code === 'MLV');
                if (mlvType) {
                    this.selectedApplicationType = mlvType;
                }
            });
    }

    get filteredApplicationTypes(): CertificateType[] {
        let filteredApplicationTypes: CertificateType[] = this.applicationTypes;

        // Filter by document type (drop-down selection)
        if (this.selectedApplicationType.code && this.selectedApplicationType.code.length > 0) {
            filteredApplicationTypes = this.applicationTypes.filter(a => a.certificateKind == this.selectedApplicationType.code);
        } else {
            // When "All documents" is selected, check if user is a seafarer
            // Seafarers can only see MLV (Seagoing vessel) and SLV (Inland vessel) document types
            const isSeafarer = userInfo.role === 'vta.mis.seafarer';

            if (isSeafarer) {
                const allowedTypes = ['MLV', 'SLV'];
                filteredApplicationTypes = this.applicationTypes.filter(a => allowedTypes.includes(a.certificateKind));
            }
        }

        // Exclude SLB (Soodustusluba/Dispensation) - handled separately elsewhere
        filteredApplicationTypes = filteredApplicationTypes.filter(a => a.certificateKind !== 'SLB');

        // Filter by search input
        if (this.searchInput.length) {
            filteredApplicationTypes = filteredApplicationTypes.filter(a => a.name.trim().toLowerCase().includes(this.searchInput.trim().toLowerCase()));
        }

        return filteredApplicationTypes;
    }

    getCertificateKind(certificateKind: string) {
        const kind = this.documentTypes.filter(t => t.code == certificateKind)[0];
        return kind ? kind.name : "";
    }

    startApplyingOrChangeType(templateId: string) {
        if (this.applicationId) {
            const request = new ChangeTypeToCertificateRequest({
                templateId,
                appId: this.applicationId
            });
            new StartApplyingOrChangeTypeClient()
                .setLoader(this.loader)
                .changeTypeToCertificate(request, this.seafarerId)
                .then(() => this.$router.go(0));
        }
        else {
            const request = new StartApplyingForCertificateRequest();
            request.templateId = templateId;
            new StartApplyingOrChangeTypeClient()
                .setLoader(this.loader)
                .startApplyingForCertificate(request, this.seafarerId)
                .then(result => this.$router.push({
                    name: "ApplicationOverview",
                    params: { applicationId: result.applicationId }
                }));
        }
    }

    applicationTypesTableFields = [
        { key: 'name', label: this.$ls.Global_Label_CertificateTypeName, thClass: 'w-50', sortable: true },
        { key: 'certificateKind', label: this.$ls.Global_Label_DocumentType, sortable: true },
        { key: 'linkIcon', label: '', sortable: false, tdClass: 'table-actions' }
    ];
}
